import { brand } from "../../../config/theme";
import { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import api from "../../../utils/api";
import { LogoSplash } from "../../../config/brandAssets";

type ProcessState = "loading" | "validating" | "closing" | "success" | "error";

export default function ConfirmSessionResetPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation("sessionReset");
  const token = searchParams.get("token");
  const hasExecuted = useRef(false);
  const [processState, setProcessState] = useState<ProcessState>("loading");
  const [closedCount, setClosedCount] = useState<number>(0);

  useEffect(() => {
    // Prevent double execution in StrictMode
    if (hasExecuted.current) return;
    hasExecuted.current = true;

    const executeSessionReset = async () => {
      // If no token, show error and redirect
      if (!token) {
        await Swal.fire({
          icon: "error",
          title: t("error"),
          text: t("invalidOrMissingToken"),
          confirmButtonText: t("goToLogin"),
          confirmButtonColor: brand.primary,
          allowOutsideClick: false,
          allowEscapeKey: false,
        });
        navigate("/");
        return;
      }

      try {
        // Step 1: Validating token
        setProcessState("validating");

        // Call the API to execute session reset
        const response = await api.post("/auth/execute-session-reset", {
          token,
        });

        // Step 2: Closing sessions
        setProcessState("closing");

        // Simulate a brief moment to show the closing state
        await new Promise((resolve) => setTimeout(resolve, 1000));

        // Step 3: Success
        const closedCount = response.data?.data?.data?.closedCount || 0;
        setClosedCount(closedCount);
        setProcessState("success");
      } catch (error: unknown) {
        setProcessState("error");

        // Determine error message
        let errorMessage = t("unknownError");

        if (error && typeof error === "object" && "response" in error) {
          const axiosError = error as {
            response?: {
              data?: {
                meta?: { message?: string };
              };
            };
          };

          const message = axiosError.response?.data?.meta?.message;

          if (message) {
            // Mapear mensajes del backend a mensajes amigables del frontend
            if (
              message.includes("ya fue utilizado") ||
              message.includes("already been used")
            ) {
              errorMessage = t("linkAlreadyUsed");
            } else if (
              message.includes("expirado") ||
              message.includes("expired") ||
              message.includes("expiró")
            ) {
              errorMessage = t("linkExpired");
            } else if (
              message.includes("inválido") ||
              message.includes("invalid") ||
              message.includes("no encontrado") ||
              message.includes("not found")
            ) {
              errorMessage = t("invalidLink");
            } else {
              errorMessage = message; // Fallback al mensaje del backend
            }
          }
        }

        await Swal.fire({
          icon: "error",
          title: t("error"),
          text: errorMessage,
          confirmButtonText: t("goToLogin"),
          confirmButtonColor: brand.primary,
          allowOutsideClick: false,
          allowEscapeKey: false,
        });

        navigate("/");
      }
    };

    executeSessionReset();
  }, [token, navigate, t]);

  const getStateContent = () => {
    switch (processState) {
      case "loading":
        return {
          title: t("pageTitle"),
          subtitle: t("pageSubtitle"),
          description: t("pleaseWait"),
          showSpinner: true,
          showIcon: false,
          showButton: false,
        };

      case "validating":
        return {
          title: t("pageTitle"),
          subtitle: t("validating"),
          description: t("validatingMessage"),
          showSpinner: true,
          showIcon: false,
          showButton: false,
        };

      case "closing":
        return {
          title: t("pageTitle"),
          subtitle: t("closingSessions"),
          description: t("closingSessionsMessage"),
          showSpinner: true,
          showIcon: false,
          showButton: false,
        };

      case "success":
        return {
          title: t("success"),
          subtitle: t("sessionsClosedSuccess", { count: closedCount }),
          description: t("canLoginAgain"),
          showSpinner: false,
          showIcon: true,
          icon: "success",
          showButton: true,
        };

      case "error":
        return {
          title: t("error"),
          subtitle: t("errorOccurred"),
          description: t("tryAgain"),
          showSpinner: false,
          showIcon: true,
          icon: "error",
          showButton: true,
        };

      default:
        return {
          title: t("pageTitle"),
          subtitle: t("pageSubtitle"),
          description: t("pleaseWait"),
          showSpinner: true,
          showIcon: false,
          showButton: false,
        };
    }
  };

  const stateContent = getStateContent();

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-100 to-gray-200 flex flex-col items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
        <div className="mb-6">
          <img src={LogoSplash} alt="Logo" className="w-32 h-32 mx-auto" />
        </div>

        <h1 className="text-2xl font-bold text-gray-800 mb-2">
          {stateContent.title}
        </h1>

        <p className="text-gray-600 mb-6">{stateContent.subtitle}</p>

        {stateContent.showSpinner && (
          <div className="flex justify-center mb-4">
            <div className="w-12 h-12 border-4 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        )}

        {stateContent.showIcon && (
          <div className="flex justify-center mb-4">
            {stateContent.icon === "success" ? (
              <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center">
                <svg
                  className="w-8 h-8 text-primary-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M5 13l4 4L19 7"
                  ></path>
                </svg>
              </div>
            ) : (
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                <svg
                  className="w-8 h-8 text-red-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  ></path>
                </svg>
              </div>
            )}
          </div>
        )}

        <p className="text-sm text-gray-500 mb-6">{stateContent.description}</p>

        {stateContent.showButton && (
          <button
            onClick={() => navigate("/")}
            className="bg-primary-500 hover:bg-[#5FE838] text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200"
          >
            {t("goToLogin")}
          </button>
        )}
      </div>
    </div>
  );
}
